export interface FontOption {
  name: string;
  fontFamily: string;
}

export const fonts: Record<string, FontOption> = {
  sourceSerifPro: {
    name: "Source Serif Pro",
    fontFamily: '"Source Serif Pro", serif',
  },
  inter: {
    name: "Inter",
    fontFamily: '"Inter", sans-serif',
  },
  manrope: {
    name: "Manrope",
    fontFamily: "Manrope, sans-serif",
  },
  poppins: {
    name: "Poppins",
    fontFamily: '"Poppins", sans-serif',
  },
  roboto: {
    name: "Roboto",
    fontFamily: '"Roboto", sans-serif',
  },
  montserrat: {
    name: "Montserrat",
    fontFamily: '"Montserrat", sans-serif',
  },
  openSans: {
    name: "Open Sans",
    fontFamily: '"Open Sans", sans-serif',
  },
  lato: {
    name: "Lato",
    fontFamily: '"Lato", sans-serif',
  },
  raleway: {
    name: "Raleway",
    fontFamily: '"Raleway", sans-serif',
  },
  workSans: {
    name: "Work Sans",
    fontFamily: '"Work Sans", sans-serif',
  },
  crimsonText: {
    name: "Crimson Text",
    fontFamily: '"Crimson Text", serif',
  },
  playfairDisplay: {
    name: "Playfair Display",
    fontFamily: '"Playfair Display", serif',
  },
  merriweather: {
    name: "Merriweather",
    fontFamily: '"Merriweather", serif',
  },
  loraSerif: {
    name: "Lora",
    fontFamily: '"Lora", serif',
  },
  ebGaramond: {
    name: "EB Garamond",
    fontFamily: '"EB Garamond", serif',
  },
  spaceMono: {
    name: "Space Mono",
    fontFamily: '"Space Mono", monospace',
  },
  firaCode: {
    name: "Fira Code",
    fontFamily: '"Fira Code", monospace',
  },
  jetBrainsMono: {
    name: "JetBrains Mono",
    fontFamily: '"JetBrains Mono", monospace',
  },
  sairaExtraCondensed: {
    name: "Saira Extra Condensed",
    fontFamily: '"Saira Extra Condensed", "Saira ExtraCondensed", serif',
  },
  // Gaming Fonts
  orbitron: {
    name: "Orbitron (Gaming)",
    fontFamily: '"Orbitron", sans-serif',
  },
  pressStart2P: {
    name: "Press Start 2P (Retro Gaming)",
    fontFamily: '"Press Start 2P", cursive',
  },
  russoOne: {
    name: "Russo One (Gaming)",
    fontFamily: '"Russo One", sans-serif',
  },
  audiowide: {
    name: "Audiowide (Gaming)",
    fontFamily: '"Audiowide", cursive',
  },
  exo2: {
    name: "Exo 2 (Gaming)",
    fontFamily: '"Exo 2", sans-serif',
  },
  rajdhani: {
    name: "Rajdhani (Gaming)",
    fontFamily: '"Rajdhani", sans-serif',
  },
  // Sports Fonts
  oswald: {
    name: "Oswald (Sports)",
    fontFamily: '"Oswald", sans-serif',
  },
  bebasNeue: {
    name: "Bebas Neue (Sports)",
    fontFamily: '"Bebas Neue", cursive',
  },
  antonio: {
    name: "Antonio (Sports)",
    fontFamily: '"Antonio", sans-serif',
  },
  barlow: {
    name: "Barlow (Sports)",
    fontFamily: '"Barlow", sans-serif',
  },
  teko: {
    name: "Teko (Sports)",
    fontFamily: '"Teko", sans-serif',
  },
  anton: {
    name: "Anton (Sports)",
    fontFamily: '"Anton", sans-serif',
  },
  righteous: {
    name: "Righteous (Sports)",
    fontFamily: '"Righteous", cursive',
  },
  bangers: {
    name: "Bangers (Sports/Comic)",
    fontFamily: '"Bangers", cursive',
  },
  // Additional Google Fonts
  notoSansJapanese: {
    name: "Noto Sans Japanese",
    fontFamily: '"Noto Sans Japanese", sans-serif',
  },
  notoSans: {
    name: "Noto Sans",
    fontFamily: '"Noto Sans", sans-serif',
  },
  ptSans: {
    name: "PT Sans",
    fontFamily: '"PT Sans", sans-serif',
  },
  quicksand: {
    name: "Quicksand",
    fontFamily: '"Quicksand", sans-serif',
  },
  robotoMono: {
    name: "Roboto Mono",
    fontFamily: '"Roboto Mono", monospace',
  },
  robotoCondensed: {
    name: "Roboto Condensed",
    fontFamily: '"Roboto Condensed", sans-serif',
  },
  playfairDisplayAlt: {
    name: "Playfair Display SC",
    fontFamily: '"Playfair Display SC", serif',
  },
  diphylleia: {
    name: "Diphylleia",
    fontFamily: '"Diphylleia", serif',
  },
  systemDefault: {
    name: "System Default",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
  },
  arial: {
    name: "Arial",
    fontFamily: 'Arial, Helvetica, "Segoe UI", sans-serif',
  },
};

export const applyFont = (fontKey: string): void => {
  const font = fonts[fontKey];
  if (!font) return;

  const root = document.documentElement;

  // Update CSS custom property for base font
  root.style.setProperty("--font-family-base", font.fontFamily);

  // Update the CSS class
  const style = document.createElement("style");
  style.id = "font-override";

  // Remove any existing font override
  const existingStyle = document.getElementById("font-override");
  if (existingStyle) {
    existingStyle.remove();
  }

  style.textContent = `
    .font_family_base {
      font-family: ${font.fontFamily} !important;
    }
  `;

  document.head.appendChild(style);

  // Save font preference to localStorage
  localStorage.setItem("fontFamily", fontKey);
};

export const loadSavedFont = (): void => {
  const savedFont = localStorage.getItem("fontFamily");
  if (savedFont && fonts[savedFont]) {
    applyFont(savedFont);
  } else {
    applyFont("arial");
  }
};

export const getCurrentFont = (): string => {
  return localStorage.getItem("fontFamily") || "arial";
};
